/**
 * AS-1834: TalentHub used to render the raw `interview_session.language`
 * code (e.g. "es-MX") in candidate detail panels because there is no DB-side
 * JOIN to `language.display_name` in the session list view. 2.0 sessions
 * land with legacy short labels ("Spanish") via `chat_session_metadata`; 3.0
 * sessions land with locale codes. This map normalizes both so recruiters
 * see a human-readable label.
 *
 * Fallback returns the raw value unchanged so any unmapped session — legacy
 * label, new locale not yet listed, or a typo'd config — does not regress
 * to blank. Add new codes here when product onboards a new locale.
 *
 * Canonical locale codes come from the backend `language` table (migration
 * 1757698697000-update-language-codes.ts). Keep keys in sync with the
 * `AvailableLanguages` enum in
 * `types/screener-builder/screener-builder-common.ts`.
 */
const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  "en-US": "English",
  en: "English",
  "es-MX": "Spanish (Mexican)",
  es: "Spanish",
  "fr-FR": "French",
  fr: "French",
  "ht-HT": "Haitian Creole",
  ht: "Haitian Creole",
};

export const formatLanguageLabel = (code: string): string =>
  LANGUAGE_DISPLAY_NAMES[code] ?? code;

/**
 * Deploy-window guard. AS-1845 flattened the API's `availableFilters.languages`
 * from `{ code, name }[]` to `string[]`. During rolling deploys, a new
 * TalentHub bundle can briefly hit an old API still emitting the legacy
 * objects. Once both services are on staging+prod, drop this helper and
 * narrow `ResultsAvailableFilters.languages` back to `string[]`.
 */
export const coerceLanguageCode = (input: string | { code: string }): string =>
  typeof input === "string" ? input : input.code;
